// File: frontend/src/components/entries/EntryImage.tsx

import React, { useState } from "react";
import { RiZoomInLine } from "react-icons/ri";
import { ImageLightbox } from "../common/ImageLightbox";
import { resolveEntryImageSrc } from "../../utils/entryImageUrl";
import "./EntryImage.css";

// Matches the (gorsel: <ref>) token used by the entry editor toolbar.
// Kept here so every render site can collect an entry's images with one helper
// instead of duplicating the pattern.
// <ref> is either an opaque image id (current format, e.g. 8f14...543.jpg) or
// a full URL (legacy entries saved before the opaque id format was introduced).
const GORSEL_URL_PATTERN = /\(gorsel:\s*(https?:\/\/[^)\s]+|[a-f0-9]{32}\.(?:jpg|jpeg|png|webp))\s*\)/g;

/**
 * Returns every image reference contained in the given entry content, in the
 * order they appear. Used to build the lightbox gallery so that arrow
 * navigation moves between the images of the same entry.
 *
 * The raw references are returned as written in the content (opaque id or
 * legacy URL); EntryImage resolves them to real src values at render time.
 */
export function collectEntryImageUrls(content: string): string[] {
  const urls: string[] = [];
  GORSEL_URL_PATTERN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = GORSEL_URL_PATTERN.exec(content)) !== null) {
    urls.push(m[1].trim());
  }
  return urls;
}

// Source of a single image token, reused by the spacing rules below.
const GORSEL_TOKEN_SOURCE =
  "\\(gorsel:\\s*(?:https?:\\/\\/[^)\\s]+|[a-f0-9]{32}\\.(?:jpg|jpeg|png|webp))\\s*\\)";

/**
 * The author decides the layout: image tokens written on the SAME line flow
 * next to each other, tokens written on separate lines stay stacked. Nothing
 * about the line breaks is touched here.
 *
 * The only correction applied is for two tokens written with no character at
 * all between them: entry content is rendered with white-space: pre-wrap, and
 * two directly adjacent inline-blocks give the browser no line-break
 * opportunity, so a long row would overflow instead of wrapping. A single
 * space is inserted between them purely as that break opportunity.
 */
const ADJACENT_IMAGE_TOKENS = new RegExp(
  `(${GORSEL_TOKEN_SOURCE})(?=${GORSEL_TOKEN_SOURCE})`,
  "g"
);

/**
 * Render-time whitespace handling for image tokens. Called by every render
 * site (EntryRow, EntryPostForm preview, DraftsPage preview, inlineMarkup)
 * before the markup patterns run.
 *
 * Every line break the author typed is left exactly as it is — the layout is
 * their decision. The only change is the safety space between two tokens that
 * were written with nothing at all between them. The stored content is never
 * modified.
 */
export function normalizeImageTokenSpacing(content: string): string {
  ADJACENT_IMAGE_TOKENS.lastIndex = 0;
  return content.replace(ADJACENT_IMAGE_TOKENS, "$1 ");
}

interface EntryImageProps {
  // Raw reference as written in the entry content: an opaque image id
  // (current format) or a full URL (legacy entries). Resolved below.
  src: string;
  // Every image of the parent entry. When it holds more than one URL the
  // lightbox shows prev/next controls and a counter; otherwise it opens as a
  // single image. Defaults to just this image.
  gallery?: string[];
  // Wrapper element. Must be "span" when the image is rendered inside an
  // inline context (e.g. nested in an alıntı/spoiler block), because a <div>
  // inside a <p>/<span> is invalid HTML and breaks the layout.
  as?: "div" | "span";
}

/**
 * Single source of truth for how an uploaded entry image is displayed.
 *
 * Previously each render site (EntryRow, EntryPostForm, DraftsPage,
 * inlineMarkup) carried its own copy of the same <img> markup, so any visual
 * fix had to be repeated four times. They all delegate here now.
 *
 * Presentation: a fixed-height framed preview with object-fit: cover, so
 * portrait and landscape photos occupy the same amount of vertical space and
 * the entry list keeps an even rhythm. Clicking opens the full image in a
 * lightbox instead of navigating away to the raw file.
 */
export const EntryImage: React.FC<EntryImageProps> = ({ src, gallery, as = "div" }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [failed, setFailed] = useState(false);

  // Both the single src and the gallery arrive as raw references, so they are
  // resolved with the same helper and stay comparable for indexOf below.
  const resolvedSrc = resolveEntryImageSrc(src);

  // Fall back to a single-image gallery when the caller did not pass one.
  const images = (gallery && gallery.length > 0 ? gallery : [src])
    .map(resolveEntryImageSrc)
    .filter((url) => url.length > 0);
  const startIndex = Math.max(images.indexOf(resolvedSrc), 0);

  // A broken/removed upload, or a reference that does not resolve to a valid
  // image id, renders nothing at all rather than a broken-image icon.
  if (failed || !resolvedSrc) return null;

  const Wrapper = as as React.ElementType;

  return (
    <Wrapper className="entry-image-frame">
      <button
        type="button"
        className="entry-image-frame__btn"
        onClick={() => setLightboxOpen(true)}
        title="büyütmek için tıkla"
        aria-label="görseli büyüt"
      >
        <img
          src={resolvedSrc}
          alt=""
          className="entry-image-frame__img"
          loading="lazy"
          onError={() => setFailed(true)}
        />
        {/* Hover affordance: dim overlay + magnifier so it is obvious the
            preview is clickable and cropped. */}
        <span className="entry-image-frame__overlay" aria-hidden="true">
          <RiZoomInLine />
        </span>
      </button>

      {lightboxOpen && (
        <ImageLightbox
          images={images}
          startIndex={startIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </Wrapper>
  );
};